import { quoteRequestApi } from 'apiConnector/quote/quote';
import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics/tabRemoved';
import type { QuoteRequestPayload } from 'models/quote/QuoteRequestPayload';
import { ofType } from 'redux-observable';
import { merge, of } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { QuoteRequested } from 'state/forms/actions/quote';
import { commonStreamActions } from 'state/forms/actions/stream';
import { getFormRequestType } from 'state/selectors';
import type { EpicWithThunk } from 'state/state.common';
import {
  staticQuoteAckedThunk,
  staticQuoteRequestFailedThunk,
  staticStreamingQuoteAckedThunk,
} from 'state/static/staticForm.thunks';
import { now } from 'utils/core/now';

function quoteCancelled(action$: Parameters<EpicWithThunk>[0], tabId: string) {
  return action$.pipe(
    ofType('QUOTE_CANCELLED'),
    filter(action => action.workspace === 'static' && action.tabId === tabId),
  );
}

export function requestQuoteEpic(): EpicWithThunk {
  return (
    action$,
    state$,
    {
      selectors: { getStaticFormInfos, getFormEnrichedData, getUserDefaultProductView },
      frontLogger,
      httpClient,
      getSgmeConfiguration,
    },
  ) =>
    action$.pipe(
      ofType('QUOTE_REQUESTED', 'STREAM_REQUESTED'),
      filter(a => a.workspace === 'static'),
      withLatestFrom(state$),
      mergeMap(([{ tabId, quoteType, quoteRequestStartingTimeInMillis, isReprice, ...action }, state]) => {
        const { quoteQuery, negotiationId, productDescription, version, sendWholeFormData, instrumentName } =
          getStaticFormInfos(state, tabId)!;
        const productView = getUserDefaultProductView(state);
        const rfq = sendWholeFormData ? getFormEnrichedData(state, tabId) : quoteQuery;
        const frontReconciliationId = (action as QuoteRequested).frontReconciliationId;
        const request: QuoteRequestPayload = {
          quoteQuery: rfq,
          negotiationId,
          productSubType: productDescription,
          instrumentContractName: productDescription,
          version,
          instrumentName,
          productView,
          tabId,
          reconciliationId: frontReconciliationId,
          isReprice,
        };
        const quoteAckTimeoutMs = getSgmeConfiguration().quoteAckTimeoutMs;

        const cancelled$ = merge(tabRemoved(action$, tabId, state$), quoteCancelled(action$, tabId));
        const requestType = getFormRequestType(state, 'static', tabId);

        return quoteRequestApi(httpClient, quoteAckTimeoutMs)
          .requestQuote(request, 'static', quoteType)
          .pipe(
            takeUntil(cancelled$),
            map(
              ({
                quoteId,
                quoteExternalId,
                pricingEstimatedDurationInMilliseconds,
                newVersion,
                isExecOnlyAddon,
                details,
                sidePanelDetails,
              }) => {
                if (requestType === 'RequestForStream') {
                  return staticStreamingQuoteAckedThunk(
                    tabId,
                    quoteId,
                    quoteType,
                    quoteExternalId,
                    negotiationId,
                    pricingEstimatedDurationInMilliseconds,
                    quoteRequestStartingTimeInMillis,
                    newVersion,
                    isExecOnlyAddon,
                  );
                } else {
                  return staticQuoteAckedThunk(
                    tabId,
                    quoteId,
                    quoteType,
                    quoteExternalId,
                    negotiationId,
                    pricingEstimatedDurationInMilliseconds,
                    quoteRequestStartingTimeInMillis,
                    newVersion,
                    isExecOnlyAddon,
                    details,
                    frontReconciliationId,
                    sidePanelDetails,
                  );
                }
              },
            ),
            catchError((error: Error) => {
              frontLogger.error('An error occurred during static quote request {@FrontError}', error);

              const translatedError = apiErrorHelper.toApiErrorMessage(error, {
                timeoutErrorMessage: 'quote.errorMessages.timeout',
              });

              const quoteRequestFailed = staticQuoteRequestFailedThunk(tabId, translatedError, frontReconciliationId);
              const streamingFailed = commonStreamActions.streamingQuoteFailed(
                'static',
                tabId,
                apiErrorHelper.getErrorRejections(error),
                now(),
              );

              if (requestType === 'RequestForStream') {
                return of(quoteRequestFailed, streamingFailed);
              }
              return of(quoteRequestFailed);
            }),
          );
      }),
    );
}
